import { ActionType, Selectors } from 'Reducers'
import { IFetchWellhubRegistrationDetailsAction, IWellhubCheckUserExistsAction, IWellhubRegisterNewUser, IWellhubSignInAction } from 'Reducers/WellhubRedux'
import { ApiService } from 'Services'
import { all, call, put, select } from 'typed-redux-saga'
import { takeLatestSafe } from 'Sagas/SagaUtils'
import { waitForOwnAuthOutcome } from 'Sagas/HelperSagas'
import { push } from 'connected-react-router'
import { getDownloadUrl } from 'Lib/EventsUtils'
import { RouteNames } from 'RouteNames'

function * handleFetchWellhubRegistrationDetailsSaga({ wellhubRegistrationId, resolve, reject }: IFetchWellhubRegistrationDetailsAction) {
  try {
    const registrationDetails = (yield call(ApiService.getWellhubRegistrationDetails, wellhubRegistrationId)) as {
      gympass_user_id: string
      email?: string
    }

    yield put({
      type: ActionType.SET_WELLHUB_REGISTRATION_DETAILS,
      gympass_user_id: registrationDetails.gympass_user_id,
      email: registrationDetails.email
    })

    if (registrationDetails.email) {
      yield call(logEvent, 'web_wellhub_newWellhubUserEmailFound')
      yield call(resolve, registrationDetails.email)
    }
  } catch (error) {
    yield call(reject, error)
    yield call(logError, 'web_handleFetchWellhubRegDetailsError', error)
  }
}

function * handleWellhubCheckUserExistsSaga({ email }: IWellhubCheckUserExistsAction) {
  try {
    yield call(logEvent, 'web_wellhub_checkUserExists')
    const exists: Awaited<ReturnType<typeof ApiService.checkEmailExists>> = yield call(ApiService.checkEmailExists, email)

    if (exists) {
      yield put(push(RouteNames.WELLHUB_WELCOME_BACK))
    } else {
      yield put(push(RouteNames.WELLHUB_REGISTER))
    }
  } catch (error) {
    yield call(logError, 'web_handleWellhubCheckUserExistsError', error)
  }
}

function * handleWellhubSignInSaga({ password, reject, resolve }: IWellhubSignInAction) {
  const wellhubEmail: ReturnType<typeof Selectors.getWellhubEmail> = yield select(Selectors.getWellhubEmail)
  const gympassUserId: ReturnType<typeof Selectors.getGympassUserId> = yield select(Selectors.getGympassUserId)

  if (!wellhubEmail) {
    yield call(logError, 'web_handleWellhubSignInError', `Wellhub email is missing for gympassUserId: ${gympassUserId}`)
    yield call(reject, Error('wellhubEmailMissing'))
    return
  }

  if (!gympassUserId) {
    yield call(logError, 'web_handleWellhubSignInError', 'Gympass user ID is missing')
    yield call(reject, Error('gympassUserIdMissing'))
    return
  }

  yield put({
    type: ActionType.SIGN_IN_WITH_EMAIL_AND_PASSWORD_REQUEST,
    email: wellhubEmail,
    password: password,
    onError: () => { },
    onSuccess: () => { }
  })

  const outcome = yield * waitForOwnAuthOutcome({
    successType: ActionType.SIGN_IN_WITH_EMAIL_AND_PASSWORD_SUCCESS,
    failedType: ActionType.SIGN_IN_WITH_EMAIL_AND_PASSWORD_FAILED,
    email: wellhubEmail,
    routePrefix: RouteNames.WELLHUB
  })

  if (outcome.status === 'abandoned') {
    yield call(logEvent, 'web_wellhub_signInAbandoned')
    return
  }

  if (outcome.status === 'success') {
    yield call(logEvent, 'web_wellhub_loginSuccess')

    const user: ReturnType<typeof Selectors.getUser> = yield select(Selectors.getUser)

    if (!user?.wellhub) {
      try {
        yield call(ApiService.registerNewWellhubUser, gympassUserId)
      } catch (error) {
        yield call(logError, 'web_wellhub_registerNewWellhubUserError', error)
      }
    }

    yield call(resolve)

    const downloadRoute: string = yield call(getDownloadUrl)
    yield put(push(downloadRoute, { hasOldSubscription: true }))
  } else {
    const error = outcome.error ?? Error('sign_in_failed')
    yield call(logError, 'web_wellhub_wellhubSignInError', error)
    yield call(reject, error)
  }
}

function * handleWellhubRegisterNewUserSaga({ name, email, password, reject }: IWellhubRegisterNewUser) {
  yield put({
    type: ActionType.REGISTER_WITH_EMAIL_AND_PASSWORD,
    name: name,
    email: email,
    password: password,
    reject: () => { },
    resolve: () => { },
  })

  const outcome = yield * waitForOwnAuthOutcome({
    successType: ActionType.REGISTER_WITH_EMAIL_AND_PASSWORD_SUCCESS,
    failedType: ActionType.REGISTER_WITH_EMAIL_AND_PASSWORD_FAILED,
    email,
    routePrefix: RouteNames.WELLHUB
  })

  if (outcome.status === 'abandoned') {
    yield call(logEvent, 'web_wellhub_registerAbandoned')
    return
  }

  if (outcome.status === 'success') {
    yield call(logEvent, 'web_wellhub_registerSuccess')

    const gympassUserId: ReturnType<typeof Selectors.getGympassUserId> = yield select(Selectors.getGympassUserId)

    if (!gympassUserId) {
      yield call(logError, 'web_wellhub_registerNewUserError', 'gympass_user_id missing')
      return
    }

    const user: ReturnType<typeof Selectors.getUser> = yield select(Selectors.getUser)

    if (!user?.wellhub) {
      try {
        yield call(ApiService.registerNewWellhubUser, gympassUserId)
      } catch (error) {
        yield call(logError, 'web_wellhub_registerNewWellhubUserError', error)
      }
    }
  } else {
    const error = outcome.error ?? Error('register_failed')

    yield call(reject, error)
    yield call(logError, 'web_wellhub_registerNewUserError', error)
  }
}

export function * WellhubSagas() {
  yield all([
    takeLatestSafe(ActionType.WELLHUB_CHECK_USER_EXISTS, handleWellhubCheckUserExistsSaga),
    takeLatestSafe(ActionType.WELLHUB_SIGN_IN, handleWellhubSignInSaga),
    takeLatestSafe(ActionType.FETCH_WELLHUB_REGISTRATION_DETAILS, handleFetchWellhubRegistrationDetailsSaga),
    takeLatestSafe(ActionType.WELLHUB_REGISTER_WITH_EMAIL_AND_PASSWORD, handleWellhubRegisterNewUserSaga),
  ])
}
